import { FindIcon } from '@/components/IconButton';
import { queryClient } from '@/graphql/client';
import { AssignProcessAnnotationsMutationVariables } from '@/graphql/codegen/graphql';
import { ASSIGN_PROCESS_ANNOTATIONS } from '@/graphql/mutations';
import { request } from '@/graphql/request';
import { useFetchProcesses } from '@/hooks/useFetchProcesses';
import { useExplore } from '@/stores/explore';
import { Annotation, useViewer } from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { debounce } from '@/utils/debounce';
import { Process } from '@/utils/process';
import { search } from '@/utils/search';
import { Button, Input, Modal, toast } from '@skand/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ProcessRow } from './ProcessRow';

interface AnnotationProcessMenuProps {
  annotations: Annotation[];
  onClose: () => void;
}

export const AnnotationProcessMenu = ({ annotations, onClose }: AnnotationProcessMenuProps) => {
  const projectId = useExplore(state => state.projectId);
  const processes = useViewer(state => state.processes);

  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [searchKey, setSearchKey] = useState<string>('');

  const { fetchProcesses } = useFetchProcesses();

  // Fetch processes and update viewer state
  useEffect(() => {
    fetchProcesses().then(processes => {
      useViewer.setState(prevState => {
        const currTarget = prevState.targetProcess;
        const targetProcess = currTarget
          ? processes.find(process => process.id === currTarget.id)
          : null;
        return { processes, targetProcess };
      });
    });
  }, [fetchProcesses]);

  const assignProcessAnnotations = useMutation({
    mutationFn: (variables: AssignProcessAnnotationsMutationVariables) =>
      request(ASSIGN_PROCESS_ANNOTATIONS, variables),
    onSuccess: async ({ submitAnnotationIdsToAnnotationProcess }) => {
      if (submitAnnotationIdsToAnnotationProcess) {
        await queryClient.invalidateQueries(useFetchProcesses.getQueryKey(projectId));
        toast({
          message: `Successfully assigned annotations to process ${submitAnnotationIdsToAnnotationProcess.id}.`,
          lifespan: 5000,
          clickToDismiss: true,
          type: 'success',
        });
        onClose();
      }
    },
  });

  // Filtered processes
  const filtered = useMemo(
    () =>
      processes.filter(process => search(process.name, searchKey) && process.kind === 'annotation'),
    [processes, searchKey],
  );

  // Debounced search handler
  const handleSearch = useMemo(() => {
    return debounce(setSearchKey, 300);
  }, [setSearchKey]);

  // Handle submitting
  const handleSubmit = () => {
    if (selectedProcess) {
      assignProcessAnnotations.mutateAsync({
        annotationIds: annotations.map(annotation => annotation.id),
        processId: selectedProcess.id,
      });
    }
  };

  return (
    <Modal className="h-[620px] w-[692px] flex">
      <></>
      <div className="mt-2 flex flex-1 flex-col justify-between px-6 py-7">
        <div className="flex flex-col gap-3">
          <div className="flex justify-between">
            <p className="color-neutral-800 typo-text-m">
              Move {annotations.length} selected annotations to a process.
            </p>
          </div>
        </div>
        <div
          className={cn(
            'h-[444px]',
            'flex',
            'flex-col',
            'rounded-lg',
            'gap-[10px]',
            'p-3',
            'border-[1px]',
            'border-solid',
            'border-neutral-400',
          )}
        >
          <div className={cn('w-full')}>
            <Input
              data-testid="input"
              label="Search"
              onChange={handleSearch}
              tail={<FindIcon />}
              value={searchKey}
            />
            <div className={cn('h-[360px]', 'flex', 'flex-col', 'overflow-y-scroll', 'mt-4')}>
              {filtered.map((process, i) => (
                <ProcessRow
                  isSelected={process === selectedProcess}
                  key={`process-${i}`}
                  onSelect={setSelectedProcess}
                  process={process}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="w-full flex gap-2">
          <Button className="w-full cursor-pointer" onClick={onClose} size="s">
            Cancel
          </Button>
          <Button
            className="w-full cursor-pointer"
            disabled={!selectedProcess}
            filled
            onClick={handleSubmit}
            primary
            size="s"
          >
            Assign
          </Button>
        </div>
      </div>
    </Modal>
  );
};
